"use client";

import { Check, NavArrowDown } from "iconoir-react";
import * as React from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface SelectInputOption {
  value: string;
  label: string;
}

export interface SelectInputOptionGroup {
  label: string;
  options: SelectInputOption[];
}

interface SelectInputProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  options?: string[];
  optionItems?: SelectInputOption[];
  optionGroups?: SelectInputOptionGroup[];
  className?: string;
  placeholder?: string;
  "aria-label"?: string;
  /**
   * When false, uses a non-modal menu so opening the list does not lock body
   * scroll and hide the scrollbar (which shifts page content horizontally).
   */
  modal?: boolean;
}

const selectTriggerClassName =
  "text-ds-body-md text-grey-900 bg-grey-0 rounded-ds-m flex h-11 w-full items-center justify-between border px-3 py-1 whitespace-nowrap border-grey-200 hover:border-grey-500 transition-colors duration-[180ms] [transition-timing-function:cubic-bezier(0.2,0.8,0.2,1)] focus-visible:border-[1.5px] focus-visible:border-transparent focus-visible:outline-none focus-visible:[background-image:linear-gradient(var(--grey-0),var(--grey-0)),var(--gradient-focus)] focus-visible:[background-clip:padding-box,border-box] focus-visible:[background-origin:border-box] focus-visible:[box-shadow:var(--shadow-focus-glow)] data-popup-open:border-[1.5px] data-popup-open:border-transparent data-popup-open:[background-image:linear-gradient(var(--grey-0),var(--grey-0)),var(--gradient-focus)] data-popup-open:[background-clip:padding-box,border-box] data-popup-open:[background-origin:border-box] data-popup-open:[box-shadow:var(--shadow-focus-glow)] disabled:bg-grey-50 disabled:border-grey-100 disabled:text-grey-300 disabled:cursor-not-allowed [&>span]:line-clamp-1 [&>svg]:transition-transform [&>svg]:duration-[180ms] [&[data-popup-open]>svg]:rotate-180";

const selectContentClassName =
  "bg-grey-0 text-grey-900 border-grey-100 shadow-ds-sm rounded-ds-m max-h-[min(var(--available-height,300px),300px)] overflow-x-hidden overflow-y-auto border py-1 data-closed:duration-[180ms] data-open:duration-[220ms] data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1";

const selectItemClassName =
  "text-ds-body-md text-grey-900 rounded-ds-s relative flex w-full cursor-default items-center py-1.5 pr-8 pl-2 outline-none select-none focus:bg-grey-50 focus:text-grey-900";

export const selectGroupLabelClassName =
  "text-grey-400 pointer-events-none px-2 pt-2 pb-1 text-[10px] leading-[1.4] font-medium tracking-[0.08em] uppercase select-none first:pt-1.5 [&:not(:first-child)]:mt-1";

function flattenOptionItems(
  optionItems: SelectInputOption[] | undefined,
  options: string[],
  optionGroups: SelectInputOptionGroup[] | undefined
): SelectInputOption[] {
  if (optionGroups?.length) {
    return optionGroups.flatMap((group) => group.options);
  }
  return (
    optionItems ?? options.map((option) => ({ value: option, label: option }))
  );
}

function renderGroupedDropdownItems(
  groups: SelectInputOptionGroup[],
  value: string,
  onChange: (v: string) => void
) {
  return groups.map((group, groupIndex) => (
    <DropdownMenuGroup key={group.label}>
      <DropdownMenuLabel className={selectGroupLabelClassName}>
        {group.label}
      </DropdownMenuLabel>
      {group.options.map((item) => {
        const selected = item.value === value;
        return (
          <DropdownMenuItem
            key={item.value}
            onClick={() => onChange(item.value)}
            className={cn(selectItemClassName, selected && "text-pulse-700")}
          >
            {item.label}
            {selected && (
              <Check
                aria-hidden
                className="text-pulse-700 absolute right-2 size-4"
              />
            )}
          </DropdownMenuItem>
        );
      })}
      {groupIndex < groups.length - 1 ? (
        <div aria-hidden className="h-1" />
      ) : null}
    </DropdownMenuGroup>
  ));
}

function renderGroupedSelectItems(groups: SelectInputOptionGroup[]) {
  return groups.map((group, groupIndex) => (
    <React.Fragment key={group.label}>
      {groupIndex > 0 ? <SelectSeparator className="my-1" /> : null}
      <SelectGroup>
        <SelectLabel className={selectGroupLabelClassName}>
          {group.label}
        </SelectLabel>
        {group.options.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectGroup>
    </React.Fragment>
  ));
}

/**
 * Convenience wrapper around `@/components/ui/select` (the same Radix-based
 * dropdown used by the /contact-us form). Keeps a simple value/onChange/options
 * API for form fields that just need a list of strings.
 */
export function SelectInput({
  id,
  value,
  onChange,
  options = [],
  optionItems,
  optionGroups,
  className,
  placeholder,
  "aria-label": ariaLabel,
  modal = true,
}: SelectInputProps) {
  const items = flattenOptionItems(optionItems, options, optionGroups);
  const selectedLabel =
    items.find((item) => item.value === value)?.label ?? value;
  const displayLabel = value ? selectedLabel : (placeholder ?? selectedLabel);
  const isPlaceholder = !value && Boolean(placeholder);

  if (!modal) {
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger
          id={id}
          type="button"
          aria-label={ariaLabel}
          className={cn(selectTriggerClassName, className)}
        >
          <span
            className={cn("line-clamp-1", isPlaceholder && "text-[#b0b3b5]")}
          >
            {displayLabel}
          </span>
          <NavArrowDown aria-hidden className="text-grey-600 size-4 shrink-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent sideOffset={4} className={selectContentClassName}>
          {optionGroups?.length
            ? renderGroupedDropdownItems(optionGroups, value, onChange)
            : items.map((item) => {
                const selected = item.value === value;
                return (
                  <DropdownMenuItem
                    key={item.value}
                    onClick={() => onChange(item.value)}
                    className={cn(
                      selectItemClassName,
                      selected && "text-pulse-700"
                    )}
                  >
                    {item.label}
                    {selected && (
                      <Check
                        aria-hidden
                        className="text-pulse-700 absolute right-2 size-4"
                      />
                    )}
                  </DropdownMenuItem>
                );
              })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        className={cn("w-full", className)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {optionGroups?.length
          ? renderGroupedSelectItems(optionGroups)
          : items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
      </SelectContent>
    </Select>
  );
}
